import { useEffect } from "react";
import ReactGA from "react-ga4";
import { useLocation } from "react-router";

const DEFAULT_TRACKING_ID = process.env.GOOGLE_ANALYTICS_MEASUREMENT_ID || "";

interface GA_DATA { 
  trackingId?: string; 
}

interface GA_EVENT { 
  action: string; 
  category: string; 
  label?: string; 
  value?: number; // e.g, the ID of the product that was clicked, the ID of the order that was paid for, etc.
}

export default function useGoogleAnalytics(data?: GA_DATA) { 
  const { trackingId } = data || {};
  const TRACKING_ID = trackingId || DEFAULT_TRACKING_ID;

  useEffect(() => { 
    if(process.env.NODE_ENV === "production") { 
      // Initialize GA4 once when the app mounts
      ReactGA.initialize(TRACKING_ID);
    
      // Send initial pageview
      ReactGA.send({ hitType: "pageview", page: window.location.pathname });
    }
  }, []);

  // Record page views
  usePageViews();
}

export function recordAnalyticsEvent(data: GA_EVENT) { 
  const { action, category, label, value } = data ?? {};

  if(process.env.NODE_ENV === "production") { 
    ReactGA.event({ category, action, label, value });
  }

  /* Usage example: 
  recordAnalyticsEvent({
    category: 'E-commerce',
    action: 'Click Add To Cart Button',
    label: 'Add Product To Cart',
    value: <productId>
  })
  */
}

/** 
 * Because React handles routing on the client side without reloading the browser, 
 * we must log pageviews manually when routes change. 
 * Since we're using react-router-dom, 
 * we track page views dynamically inside a custom hook:
 */
function usePageViews() {
  const location = useLocation();

  useEffect(() => { 
    if(process.env.NODE_ENV === "production") { 
      ReactGA.send({ 
        hitType: "pageview", 
        page: location.pathname + (location.search ?? ""), 
      });
    }
  }, [location]);
}
